import { useState } from "react";
import { motion } from "framer-motion";
import { IMAGES } from "@/data/site";
import { ExternalLink, Play } from "lucide-react";
import { LazyYouTube } from "./LazyYouTube";

const PLAYLIST_ID = "PL-t8V4eW2g_vvhqExmZfFa94s8--82kaL";

// Young Moguls Marketing Commercials — curated 4-up reel
const COMMERCIALS = [
    {
        id: "p2E5dg3Z7OU",
        title: "Encina High Bulldogs Bark Intro",
        tag: "Encina · Intro",
    },
    {
        id: "_6_kZ5UrMaA",
        title: "Valley High QB Challenge",
        tag: "Valley High · QB",
    },
    {
        id: "l_YWco1qv-k",
        title: "Sacramento Charter B-Ball Challenge",
        tag: "Charter High · Hoops",
    },
    {
        id: "RgAeXFOPXvs",
        title: "San Juan High B-Ball Challenge",
        tag: "San Juan · Hoops",
    },
];

export const Experience = () => {
    const [activeId, setActiveId] = useState(COMMERCIALS[0].id);
    const active =
        COMMERCIALS.find((v) => v.id === activeId) ?? COMMERCIALS[0];

    const selectVideo = (id) => {
        setActiveId(id);
    };

    return (
        <section
            id="marketing"
            data-testid="experience-section"
            className="relative py-12 md:py-16 pb-6 md:pb-8 bg-[#050505] overflow-hidden [contain:layout_paint] transform-gpu"
        >
            <div
                aria-hidden
                className="absolute inset-0 opacity-[0.04] pointer-events-none"
                style={{
                    backgroundImage: `url(${IMAGES.waveform})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                }}
            />

            <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 relative">
                <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-6">
                    <div className="max-w-3xl">
                        <span className="text-xs uppercase tracking-[0.4em] text-[#D4AF37] font-bold">
                            / 03 — Marketing
                        </span>
                        <h2 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tight leading-[0.95]">
                            Class Lights.
                            <br />
                            <span className="gold-text-gradient">
                                Spot Lights.
                            </span>
                        </h2>
                        <p className="mt-6 text-white/75 leading-relaxed text-base md:text-lg">
                            Young Moguls experience the complete marketing
                            pipeline — from{" "}
                            <span className="text-[#D4AF37]">scriptwriting</span>{" "}
                            and{" "}
                            <span className="text-[#D4AF37]">shot-listing</span>{" "}
                            to{" "}
                            <span className="text-[#D4AF37]">directing</span>,{" "}
                            <span className="text-[#D4AF37]">acting</span>, and{" "}
                            <span className="text-[#D4AF37]">graphic design</span>.
                            Every commercial that rolls is shaped by a
                            youth-led crew learning the language of campaigns
                            the way the industry actually runs them.
                        </p>
                    </div>
                    <a
                        href={`https://www.youtube.com/playlist?list=${PLAYLIST_ID}`}
                        target="_blank"
                        rel="noreferrer"
                        data-testid="experience-yt-link"
                        className="inline-flex items-center gap-2 text-sm uppercase tracking-[0.25em] text-white/70 hover:text-[#D4AF37] transition-colors"
                    >
                        Watch The Playlist
                        <ExternalLink size={14} />
                    </a>
                </div>

                {/* Featured commercial big-screen */}
                <div className="relative aspect-video w-full overflow-hidden border border-[#D4AF37]/30 bg-black mb-3 group">
                    <LazyYouTube
                        videoId={activeId}
                        playlistId={PLAYLIST_ID}
                        title={active.title}
                        testId="marketing-featured"
                    />
                </div>

                {/* Now playing strip */}
                <div className="flex items-center justify-between mb-6 text-xs uppercase tracking-[0.25em] text-white/55">
                    <div className="flex items-center gap-3">
                        <span className="flex items-center gap-2">
                            <span className="h-1.5 w-1.5 rounded-full bg-[#D4AF37] animate-pulse" />
                            <span className="text-[#D4AF37] font-bold">
                                Now Rolling
                            </span>
                        </span>
                        <span className="text-white/80 normal-case text-sm font-display uppercase tracking-tight">
                            "{active.title}"
                        </span>
                    </div>
                    <span className="hidden sm:block">
                        {COMMERCIALS.length} commercials in rotation
                    </span>
                </div>

                {/* Commercial thumbnail cycle row */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-14">
                    {COMMERCIALS.map((v, i) => {
                        const isActive = v.id === activeId;
                        return (
                            <motion.button
                                key={v.id}
                                whileHover={{ y: -3 }}
                                onClick={() => selectVideo(v.id)}
                                data-testid={`marketing-thumb-${i}`}
                                aria-label={`Play ${v.title}`}
                                className={`relative aspect-video overflow-hidden text-left group transition-all duration-300 ${
                                    isActive
                                        ? "border-2 border-[#D4AF37] gold-glow"
                                        : "border border-white/10 hover:border-[#D4AF37]/60"
                                }`}
                            >
                                <img
                                    src={`https://img.youtube.com/vi/${v.id}/hqdefault.jpg`}
                                    alt={v.title}
                                    loading="lazy"
                                    className={`absolute inset-0 w-full h-full object-cover transition-all duration-500 ${
                                        isActive
                                            ? ""
                                            : "grayscale-[60%] group-hover:grayscale-0"
                                    }`}
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />

                                {!isActive && (
                                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                        <span className="h-10 w-10 rounded-full bg-[#D4AF37] grid place-items-center">
                                            <Play
                                                size={14}
                                                className="text-black ml-0.5"
                                            />
                                        </span>
                                    </div>
                                )}

                                {isActive && (
                                    <div className="absolute top-2 left-2 z-10 px-2 py-0.5 bg-[#D4AF37] text-black text-[9px] uppercase tracking-[0.2em] font-bold">
                                        On Air
                                    </div>
                                )}

                                <div className="absolute bottom-0 left-0 right-0 p-3 z-10">
                                    <div className="text-[9px] uppercase tracking-[0.3em] text-[#D4AF37]">
                                        {v.tag}
                                    </div>
                                    <div className="font-display text-sm uppercase tracking-tight text-white mt-1 leading-tight line-clamp-1">
                                        {v.title}
                                    </div>
                                </div>
                            </motion.button>
                        );
                    })}
                </div>

                {/* Production pipeline icons removed */}
            </div>
        </section>
    );
};
